// BeatClock — audio-clock-driven beat generator (singleton). Timing is derived from
// AudioContext.currentTime (via the audio engine), NOT accumulated setInterval ticks, so it is
// drift-free by construction. It only runs while real audio is playing; it never starts a silent
// AudioContext just to animate, and produces no rhythm for ordinary users.
import audioManager from "./AudioManager";

class BeatClock {
  constructor() {
    if (BeatClock._i) return BeatClock._i;
    this.bpm = 72;
    this.beatsPerBar = 4;
    this.barsPerPhrase = 4;
    this.running = false;
    this.paused = false;
    this.subs = new Set();
    this._raf = null;
    this._startAudioTime = 0;
    this._beat = -1;
    this.drift = 0;
    this.resyncs = 0;
    BeatClock._i = this;
  }

  configure({ bpm, beatsPerBar, barsPerPhrase } = {}) {
    if (bpm && bpm > 0) this.bpm = bpm;
    if (beatsPerBar) this.beatsPerBar = beatsPerBar;
    if (barsPerPhrase) this.barsPerPhrase = barsPerPhrase;
  }

  subscribe(fn) { this.subs.add(fn); return () => this.subs.delete(fn); }
  _emit(type, extra) {
    const ev = { type, beat: this.beatIndex(), bar: this.barIndex(), phrase: this.phraseIndex(), at: Date.now(), ...extra };
    this.subs.forEach((fn) => { try { fn(ev); } catch (e) { /* isolate */ } });
  }

  _audioTime() {
    const ctx = audioManager.ctx;
    return ctx ? ctx.currentTime : null;
  }

  beatIndex() { return this._beat < 0 ? 0 : this._beat; }
  barIndex() { return Math.floor(this.beatIndex() / this.beatsPerBar); }
  phraseIndex() { return Math.floor(this.beatIndex() / (this.beatsPerBar * this.barsPerPhrase)); }

  // Only starts if a real audio clock exists (audio actually playing). No fake rhythm otherwise.
  start() {
    if (this.running) return false;
    const t = this._audioTime();
    if (t == null) return false; // no audio clock → caller uses immediate fallback
    this.running = true;
    this.paused = false;
    this._startAudioTime = t;
    this._beat = -1;
    this._loop();
    return true;
  }

  _loop() {
    if (!this.running) return;
    this._raf = requestAnimationFrame(() => this._tick());
  }

  _tick() {
    if (!this.running || this.paused) { this._loop(); return; }
    const t = this._audioTime();
    if (t == null) { this.stop(); return; }
    const secPerBeat = 60 / this.bpm;
    const elapsed = t - this._startAudioTime;
    const beat = Math.floor(elapsed / secPerBeat);
    if (beat > this._beat) {
      // drift = how late this frame fired vs the ideal beat boundary
      this.drift = Math.round((elapsed - beat * secPerBeat) * 1000);
      this._beat = beat;
      this._emit("beat");
      this._emit("half_beat");
      if (beat % this.beatsPerBar === 0) this._emit("bar_start");
      if (beat % (this.beatsPerBar * this.barsPerPhrase) === 0) this._emit("phrase_start");
    }
    this._loop();
  }

  pause() { this.paused = true; }
  resume() { if (this.running) this.paused = false; }
  // Resynchronize to the current audio clock exactly once (e.g. after PWA resume/reconnect).
  resync() {
    const t = this._audioTime();
    if (t == null) return false;
    this._startAudioTime = t - this.beatIndex() * (60 / this.bpm);
    this.resyncs += 1;
    return true;
  }

  stop() {
    this.running = false;
    this.paused = false;
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
    this._beat = -1;
  }

  status() {
    return {
      running: this.running, paused: this.paused, bpm: this.bpm,
      beatsPerBar: this.beatsPerBar, barsPerPhrase: this.barsPerPhrase,
      beat: this.beatIndex(), bar: this.barIndex(), phrase: this.phraseIndex(),
      drift: this.drift, resyncs: this.resyncs, healthy: this.running && !this.paused,
      audioClock: this._audioTime() != null,
    };
  }

  // Founder test hook — inject a synthetic event without real audio (diagnostics only).
  emitManual(type) { this._emit(type, { manual: true }); }
}

export const beatClock = new BeatClock();
export default beatClock;
